import type { CityConfig } from "../city-template/types";

export const newarkDeConfig: CityConfig = {
  city: "Newark",
  state: "Delaware",
  stateAbbr: "DE",
  slug: "newark-de",
  route: "/cities/newark-de",
  title: "Emergency Passport Newark DE | Expedited Passport University of Delaware Study Abroad | Fast Passport Center",
  metaDescription: "Emergency passport and expedited passport services in Newark, DE — as fast as 24 hours. Fast passport renewal for University of Delaware students, study abroad travelers, and New Castle County residents near the Maryland border. A+ BBB rated.",
  h1: "Emergency & Expedited Passport Services in Newark, DE",
  heroSubtitle: "UD student needing a study abroad passport? Corporate traveler near the Maryland border? Fast Passport Center helps Newark, Delaware and southern New Castle County residents get their expedited passports as quickly as 24 hours. A+ BBB rated.",
  heroImageUrl: "https://readdy.ai/api/search-image?query=University%20of%20Delaware%20historic%20red%20brick%20Main%20Building%20with%20its%20iconic%20white%20steeple%20on%20the%20Newark%20Delaware%20campus%2C%20surrounded%20by%20green%20trees%20in%20warm%20late%20afternoon%20sunlight%2C%20autumn%20leaves%20beginning%20to%20change%20on%20surrounding%20oaks%20and%20maples%2C%20classic%20collegiate%20architecture%20with%20stone%20paths%20and%20green%20lawns%2C%20no%20visible%20people%20or%20text%2C%20professional%20architectural%20photography%2C%20wide%20view%2C%20photorealistic%20style&width=1800&height=700&seq=newark-de-hero-001&orientation=landscape",
  heroImageAlt: "University of Delaware historic Main Building with white steeple on the Newark Delaware campus",
  geoPosition: "39.6837;-75.7497",
  geoPlacename: "Newark, Delaware",
  lastModified: "2026-06-01",
  regionalAgency: {
    name: "Philadelphia Passport Agency",
    address: "600 Arch St Suite 4200, Philadelphia, PA 19106",
    distance: "Approximately 40–50 minutes from Newark, DE via I-95 N",
  },
  nearbyCitiesZips: [
    { name: "Newark", zips: "19702, 19711, 19713, 19716" },
    { name: "Bear", zips: "19701" },
    { name: "Glasgow", zips: "19702" },
    { name: "Christiana", zips: "19702" },
    { name: "Middletown", zips: "19709" },
    { name: "Elkton (MD)", zips: "21921" },
    { name: "Rising Sun (MD)", zips: "21911" },
    { name: "North East (MD)", zips: "21901" },
    { name: "Hockessin", zips: "19707" },
    { name: "Pike Creek", zips: "19808" },
    { name: "Landenberg (PA)", zips: "19350" },
    { name: "Oxford (PA)", zips: "19363" },
    { name: "Avondale (PA)", zips: "19311" },
  ],
  benefits: [
    { icon: "ri-time-line", title: "24–48 Hour Processing", desc: "Emergency courier for UD study abroad and Newark area departures" },
    { icon: "ri-shield-check-line", title: "Registered Courier", desc: "U.S. Department of State approved passport expediting" },
    { icon: "ri-graduation-cap-line", title: "Study Abroad Specialist", desc: "We understand semester deadlines and program requirements" },
    { icon: "ri-mail-send-line", title: "Document Shipping", desc: "Prepaid overnight labels — no trip to Philadelphia" },
  ],
  internalLinks: [
    { name: "Expedited passport in Wilmington, DE", route: "/cities/wilmington-de" },
    { name: "Emergency passport help in Dover, DE", route: "/cities/dover-de" },
    { name: "Urgent passport services in Philadelphia", route: "/cities/philadelphia" },
    { name: "Delaware passport services overview", route: "/states/delaware" },
  ],
  fpcOfficeNote: "Fast Passport Center serves Newark, Delaware remotely via secure registered courier. We do not have a walk-in office in Newark — our registered couriers hand-deliver your documents to U.S. Department of State processing facilities. This is how we achieve 24–48 hour turnaround for Newark and southern New Castle County travelers without the drive to Philadelphia.",
  fpcOfficeAddress: "1700 Market St, Suite 1005, Philadelphia, PA 19103",
  fpcOfficeDistance: "Serving Newark, Bear, Glasgow, and southern New Castle County",
  serviceAreaIntro: "Fast Passport Center provides emergency and expedited passport services throughout the Newark, Delaware area and southern New Castle County. From the University of Delaware main campus to the Route 40 corridor, from Bear and Glasgow to communities near the Maryland border — we serve all of Newark's diverse residential, academic, and corporate communities.",
  nearbyCitiesIntro: "We serve Newark and nearby communities including Bear, Glasgow, Christiana, Middletown, Hockessin, Pike Creek, and cross-border communities in Cecil County, Maryland (Elkton, North East, Rising Sun) and Chester County, Pennsylvania (Landenberg, Oxford, Avondale). If you are within 30 minutes of Newark, we have you covered.",
  howItWorksIntro: "Most Newark travelers — including UD students heading to study abroad — complete everything online and ship documents overnight. No need to drive to Philadelphia or take time off from classes and work.",
  mistakesIntro: "Newark, Delaware has a unique mix of university students, corporate technology workers, and cross-border Maryland and Pennsylvania residents. Here are the most common passport mistakes we see from Newark travelers.",
  testimonialsIntro: "Newark, Delaware travelers — from UD students to corporate commuters — trust us when their departure is days away.",
  faqIntro: "Have questions about expedited passports in Newark, DE? Here are the most common ones we hear from university and residential travelers.",
  statePageRoute: "/states/delaware",
  statePageName: "Delaware",
  dmvWebsite: "https://www.dmv.de.gov",
  dmvLabel: "Delaware DMV — Official Website",
  stickyNavLinks: [
    { label: "Processing", href: "#processing-options" },
    { label: "Services", href: "#services" },
    { label: "Service Area", href: "#service-area" },
    { label: "Facilities", href: "#acceptance-facilities" },
    { label: "Comparison", href: "#comparison" },
    { label: "How It Works", href: "#how-it-works" },
    { label: "FAQ", href: "#faq" },
    { label: "Resources", href: "#local-resources" },
  ],
  processingOptions: [
    {
      speed: "2–9",
      unit: "Business Days",
      label: "Fast Passport Center",
      tag: "Most Popular",
      tagColor: "bg-[#F6C344] text-[#1A365D]",
      features: ["Registered U.S. Department of State Courier", "Reserved agency submission", "Predictable turnaround", "Perfect for study abroad deadlines", "Document review included", "Dedicated agent assigned to your case", "Daily status updates — never left guessing", "Not affected by government shutdowns"],
      highlight: true,
    },
    {
      speed: "3–5",
      unit: "Weeks",
      label: "Fast Passport Center",
      tag: "Standard Expedited",
      tagColor: "bg-[#1A365D]/10 text-[#1A365D]",
      features: ["Registered U.S. Department of State Courier", "Reserved agency submission", "Faster than self-service", "Good for planned travel", "Document review included", "Dedicated agent assigned to your case", "Daily status updates — never left guessing", "Not affected by government shutdowns"],
      highlight: false,
      smartSaver: true,
    },
    {
      speed: "1–2",
      unit: "Months",
      label: "Self-Service (Post Office)",
      tag: "Slowest",
      tagColor: "bg-gray-100 text-gray-400",
      features: ["No courier access — standard mail only", "Workload & wait times shift with shutdowns and budget fights", "In-person clerk appointment required", "No document review — errors cause rejection", "No dedicated agent — you are on your own", "Updates only by physical mail letter", "No phone or email contact for your case", "Can't upgrade speed after submission", "Not suitable if travel is within 10 weeks"],
      highlight: false,
      muted: true,
    },
    {
      speed: "24",
      unit: "Hours*",
      label: "Regional Agency Processing",
      tag: "Emergency Only",
      tagColor: "bg-gray-100 text-gray-500",
      features: ["Appointment required — no walk-ins allowed", "Life-or-death emergencies only", "Must drive 40–50 min to Philadelphia", "Full day commitment — challenging for students"],
      highlight: false,
      emergency: true,
    },
  ],
  services: [
    {
      icon: "ri-flashlight-line",
      title: "Emergency Passport",
      description: "For Newark travelers with immediate departure needs — as fast as 24 hours. Perfect for last-minute study abroad departures.",
      bullets: ["24–48 hour turnaround available", "Registered courier hand-delivery", "Study abroad program support"],
      badge: "Most Popular",
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-passport-line",
      title: "Passport Renewal",
      description: "Fast renewal for Newark adults and returning UD students with a passport issued within the last 15 years.",
      bullets: ["No clerk visit required for most renewals", "Mail-in or courier options", "Returned to Newark address"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=passport-renewal",
    },
    {
      icon: "ri-add-circle-line",
      title: "New Passport (First-Time)",
      description: "First-time passport application for UD students and Newark residents — including study abroad and first international trips.",
      bullets: ["Acceptance facility scheduling near UD", "Form DS-11 assistance", "Photo requirement guidance"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-alert-line",
      title: "Lost or Stolen Passport",
      description: "Emergency passport replacement for Newark and southern New Castle County residents.",
      bullets: ["DS-64 form assistance", "Identity verification support", "Emergency replacement in 24–48 hours"],
      badge: "Urgent",
      link: "https://fastpassportcenter.com/checkout/?passport=lost-stolen-passport",
    },
    {
      icon: "ri-parent-line",
      title: "Child Passport",
      description: "Passport services for minors under 16 — for Newark families with kids traveling internationally.",
      bullets: ["DS-11 form for minors", "Parental consent guidance", "Both parents presence rules"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=child-passport",
    },
    {
      icon: "ri-file-edit-line",
      title: "Name Change Passport",
      description: "Passport name correction after marriage, divorce, or legal name changes for Newark Delaware residents.",
      bullets: ["DS-5504 or DS-82 guidance", "Marriage certificate support", "Court order documentation"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=name-change",
    },
  ],
  servicesSchema: [
    { name: "Emergency Passport Newark DE", description: "Urgent passport processing for Newark Delaware travelers and University of Delaware students with immediate departure needs." },
    { name: "Study Abroad Passport Expediting Newark DE", description: "Fast passport services for University of Delaware students departing for study abroad programs." },
    { name: "Expedited Passport Renewal Newark DE", description: "Fast passport renewal for Newark Delaware residents with a previously-issued passport." },
    { name: "Child Passport Newark DE", description: "Minor passport services for Newark Delaware families with children under 16." },
  ],
  testimonials: [
    {
      name: "Tyler M.",
      location: "Newark, DE (UD Student)",
      rating: 5,
      text: "Found out my passport was expired two weeks before my UD study abroad semester in Florence. Fast Passport Center had my new passport in 4 days. Genuinely saved my semester abroad.",
      service: "Emergency Passport",
    },
    {
      name: "Dr. Patricia R.",
      location: "Newark, DE",
      rating: 5,
      text: "Conference in Tokyo with 10 days notice and my passport had expired. They got me a renewed passport in 3 business days. Impeccable service from a Newark faculty perspective.",
      service: "Passport Renewal",
    },
    {
      name: "Kevin L.",
      location: "Bear, DE",
      rating: 5,
      text: "Lost my passport before a family trip to Ireland. Fast Passport Center handled the DS-64, guided me through everything, and delivered a replacement in 2 days. Outstanding.",
      service: "Lost Passport",
    },
    {
      name: "Susan C.",
      location: "Elkton, MD (near Newark, DE)",
      rating: 5,
      text: "We live right on the Delaware-Maryland border and discovered Fast Passport Center when we needed expedited passports for our whole family. Three passports, four business days. Incredible.",
      service: "Expedited Renewal",
    },
  ],
  facilities: [
    {
      id: "newark-de-1",
      name: "Newark Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "115 E Main St",
      city: "Newark",
      state: "DE",
      zip: "19711",
      phone: "(302) 369-0290",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "Near downtown Newark and University of Delaware",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Primary acceptance facility for Newark. Popular for University of Delaware students. Appointments required — book 1–2 weeks in advance.",
      icon: "ri-mail-line",
    },
    {
      id: "newark-de-2",
      name: "Ogletown Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "1501 Ogletown Rd",
      city: "Newark",
      state: "DE",
      zip: "19711",
      phone: "(302) 368-3730",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "Serving Ogletown and south Newark",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Convenient for residents along the Route 40 corridor. Appointments required.",
      icon: "ri-mail-line",
    },
    {
      id: "newark-de-3",
      name: "Bear Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "315 Middletown Rd",
      city: "Bear",
      state: "DE",
      zip: "19701",
      phone: "(302) 836-0162",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "Serving Bear and Glasgow communities east of Newark",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Convenient for Bear and Glasgow residents. Call to confirm current passport acceptance hours.",
      icon: "ri-mail-line",
    },
    {
      id: "newark-de-4",
      name: "Christiana Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "1700 Capitol Trail",
      city: "Newark",
      state: "DE",
      zip: "19702",
      phone: "(302) 738-7680",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "Near Christiana Mall and I-95 corridor",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Convenient for Christiana and Route 1 corridor residents. Very busy — book appointments well in advance.",
      icon: "ri-mail-line",
    },
    {
      id: "newark-de-5",
      name: "New Castle County Clerk of Peace",
      type: "County Clerk Passport Acceptance",
      address: "800 N French St",
      city: "Wilmington",
      state: "DE",
      zip: "19801",
      phone: "(302) 395-7799",
      hours: "Mon–Fri: 8:00 AM – 4:00 PM",
      distance: "15 miles from Newark in Wilmington",
      website: "https://www.nccde.org/",
      websiteLabel: "New Castle County",
      notes: "County-wide facility with full passport acceptance. Slightly further but offers extended hours compared to post office locations.",
      icon: "ri-government-line",
    },
    {
      id: "newark-de-6",
      name: "University of Delaware",
      type: "Note: On-Campus Resource",
      address: "18 Amstel Ave, Newark, DE 19716",
      city: "Newark",
      state: "DE",
      zip: "19716",
      phone: "(302) 831-2115",
      hours: "Mon–Fri: 8:30 AM – 5:00 PM",
      distance: "On the University of Delaware main campus",
      website: "https://global.udel.edu/",
      websiteLabel: "UD Office of Global Affairs",
      notes: "The UD Office of Global Affairs helps students with study abroad passport guidance but does not process passport applications. Use an acceptance facility listed above for your actual application.",
      icon: "ri-school-line",
    },
  ],
  comparisonRows: [
    { feature: "Appointment Needed", agency: "Yes — call NPIC, limited Philadelphia slots", fpc: "No — we handle everything for you" },
    { feature: "Processing Speed", agency: "Same-day if approved (rare)", fpc: "24–48 hours guaranteed for emergency" },
    { feature: "Document Review", agency: "No — rejected if wrong", fpc: "Yes — we check before submission" },
    { feature: "Travel to Agency", agency: "Yes — 40–50 min drive to Philadelphia", fpc: "No — ship from Newark or Bear" },
    { feature: "Study Abroad Deadlines", agency: "No flexibility for academic programs", fpc: "Yes — we understand semester schedules" },
    { feature: "Agent Support", agency: "No — government clerk only", fpc: "Yes — dedicated agent assigned" },
    { feature: "Status Updates", agency: "No — no tracking provided", fpc: "Yes — daily email updates" },
  ],
  mistakes: [
    {
      icon: "ri-graduation-cap-line",
      title: "Waiting Until the Week Before Study Abroad",
      description: "University of Delaware students often discover their passport is expired or never applied one to two weeks before departure. Expedited service is available but applies early — study abroad programs rarely delay for passport issues.",
    },
    {
      icon: "ri-image-line",
      title: "Wrong Photo Size or Background",
      description: "Campus photo services sometimes produce photos that meet general ID requirements but not passport standards. Passport photos must be exactly 2x2 inches with a white or off-white background.",
    },
    {
      icon: "ri-file-list-line",
      title: "Incomplete DS-11 Form",
      description: "Missing signatures or using the wrong form type are the #1 rejection reasons at Newark and New Castle County acceptance facilities.",
    },
    {
      icon: "ri-map-pin-2-line",
      title: "Confusion About Newark, NJ vs Newark, DE",
      description: "Newark, Delaware is entirely separate from Newark, New Jersey. Make sure all documents, addresses, and applications clearly specify Wilmington DE or Newark DE to avoid processing confusion.",
    },
    {
      icon: "ri-money-dollar-circle-line",
      title: "Wrong Payment Amount",
      description: "Government fees change periodically. Always verify current fees before submitting — incorrect payment means starting over.",
    },
    {
      icon: "ri-time-line",
      title: "Underestimating Routine Processing Time",
      description: "Newark residents often assume 4–6 weeks is enough. Routine processing takes 6–13 weeks. Start expedited service at least 6 weeks before your study abroad or travel departure.",
    },
  ],
  steps: [
    { number: "1", icon: "ri-file-list-line", title: "Apply Online", description: "Complete your application on our secure website in under 10 minutes — from your dorm, apartment, or home." },
    { number: "2", icon: "ri-mail-send-line", title: "Ship Documents", description: "We send you a prepaid overnight label. Drop your documents at any UPS location near the UD campus or in Newark." },
    { number: "3", icon: "ri-shield-check-line", title: "We Review", description: "Our team checks every document for accuracy — no rejections for small errors." },
    { number: "4", icon: "ri-passport-line", title: "Processing", description: "Your passport is processed at a U.S. Department of State facility using our registered courier slots." },
    { number: "5", icon: "ri-truck-line", title: "Delivery", description: "We overnight your new passport back to your Newark address — trackable every step of the way." },
  ],
  faqs: [
    { q: "How do University of Delaware students get emergency passports for study abroad?", a: "Fast Passport Center specializes in emergency and expedited passports for UD students departing for study abroad programs. We can process first-time passports, renewals, and replacements in as little as 24–48 hours. Contact us as soon as you know your program departure date — the earlier the better, but we can handle last-minute cases too." },
    { q: "How fast can I get an emergency passport in Newark, DE?", a: "Fast Passport Center offers emergency passport processing in Newark, Delaware in as little as 24–48 hours. Our registered couriers hand-deliver your documents to the Philadelphia Passport Agency (40–50 minutes from Newark) or other State Department facilities — without you needing to make the drive." },
    { q: "Can I get an expedited passport for study abroad near the University of Delaware?", a: "Absolutely. We help UD students with all passport situations — first-time applications for study abroad, emergency renewals for expiring passports, and replacement of lost or stolen passports. We understand program departure deadlines and provide the fastest possible service." },
    { q: "Does Newark, Delaware have its own passport agency?", a: "No — neither Newark, Delaware nor the state of Delaware has a regional passport agency. The closest facility is the Philadelphia Passport Agency, approximately 40–50 minutes from Newark via I-95. Fast Passport Center's courier service means Newark residents never need to make that drive." },
    { q: "Can I get a passport if I live near the Maryland-Delaware border?", a: "Yes. Fast Passport Center serves cross-border communities including Elkton, North East, and Rising Sun in Cecil County, Maryland, as well as Oxford and Landenberg in Chester County, Pennsylvania. If you are within 30–40 minutes of Newark, Delaware, our service works the same way regardless of which side of the border you live on." },
    { q: "How much does an expedited student passport cost in Newark, DE?", a: "Government fees are fixed: $130 for a first-time passport book or $130 + $60 expedite for a renewed adult passport. Fast Passport Center's service fees vary by processing speed. We offer payment flexibility for students. Contact us for a quote." },
    { q: "Is there a UD-specific passport service?", a: "The University of Delaware Office of Global Affairs provides guidance and checklists for study abroad students but does not process passport applications. Fast Passport Center works directly with UD students to provide fast, reliable expedited passport processing — from first-time applications to emergency renewals." },
    { q: "What airports do Newark, Delaware travelers use?", a: "Most Newark residents fly internationally through Philadelphia International Airport (PHL), approximately 40–50 minutes north via I-95. Baltimore-Washington International (BWI) is accessible for those in the western parts of New Castle County. Always verify your passport is valid for at least 6 months past your return date." },
  ],
  photoLocations: [
    { name: "Walgreens — Newark", address: "100 E Main St, Newark, DE 19711", hours: "Mon–Sun: 7AM–10PM" },
    { name: "CVS Pharmacy — Newark", address: "230 E Main St, Newark, DE 19711", hours: "Mon–Sun: 8AM–10PM" },
    { name: "UPS Store — Newark", address: "1223 S College Ave, Newark, DE 19713", hours: "Mon–Fri: 8AM–7PM, Sat: 9AM–5PM" },
    { name: "Walmart — Newark", address: "400 Ogletown Rd, Newark, DE 19711", hours: "Mon–Sun: 6AM–11PM" },
    { name: "FedEx Office — Christiana", address: "630 S College Ave, Newark, DE 19713", hours: "Mon–Fri: 8AM–8PM, Sat: 9AM–6PM" },
  ],
  dmvLocations: [
    { name: "DMV — Newark", address: "723 W Main St, Newark, DE 19702", hours: "Mon–Fri: 8AM–4:30PM", note: "Serves Newark and southern New Castle County. Convenient for University of Delaware area residents. Appointments recommended.", featured: true },
    { name: "DMV — Wilmington", address: "2230 Hessler Blvd, Wilmington, DE 19808", hours: "Mon–Fri: 8AM–4:30PM", note: "Alternative for northern New Castle County residents. REAL ID processing available.", featured: false },
  ],
  airports: [
    {
      code: "PHL",
      name: "Philadelphia International Airport",
      distance: "40–50 miles from Newark, DE",
      address: "8000 Essington Ave, Philadelphia, PA 19153",
      note: "Primary international departure airport for Newark travelers. 40–50 minute drive north on I-95. Major international hub with direct flights to Europe, the Caribbean, and beyond.",
      flightUrl: "https://www.phl.org/",
      flightLabel: "PHL Flight Info",
      featured: true,
    },
    {
      code: "ILG",
      name: "Wilmington Airport (New Castle)",
      distance: "15 miles from Newark, DE",
      address: "141 N DuPont Hwy, New Castle, DE 19720",
      note: "Closest airport to Newark with limited domestic service. No major international connections.",
      flightUrl: "https://www.flyilg.com/",
      flightLabel: "ILG Airport",
      featured: false,
    },
    {
      code: "BWI",
      name: "Baltimore-Washington International",
      distance: "60–70 miles from Newark, DE",
      address: "7062 Elm Rd, Baltimore, MD 21240",
      note: "Alternative option, especially for budget airlines and travelers near the Maryland border.",
      flightUrl: "https://www.bwiairport.com/",
      flightLabel: "BWI Airport",
      featured: false,
    },
  ],
  vitalRecordsIcon: "ri-heart-pulse-line",
  vitalRecordsLinks: [
    { icon: "ri-user-smile-line", label: "Birth Certificate", desc: "Request certified Delaware birth certificates from the Delaware Office of Vital Statistics. Available by mail, online, or in person at the Dover office.", url: "https://dhss.delaware.gov/dph/ss/vitalstats.html" },
    { icon: "ri-file-list-2-line", label: "Death Certificate", desc: "Obtain certified death certificates through the Delaware Office of Vital Statistics. Mail, online, and in-person options available.", url: "https://dhss.delaware.gov/dph/ss/vitalstats.html" },
    { icon: "ri-heart-pulse-line", label: "Marriage Certificate", desc: "Request certified marriage certificates from the New Castle County Clerk of the Peace. Marriage records maintained by the county where the ceremony took place.", url: "https://www.nccde.org/" },
    { icon: "ri-file-copy-2-line", label: "Divorce Decree", desc: "Obtain certified divorce decrees through the New Castle County Superior Court Prothonotary. Divorce records filed in the county where the decree was granted.", url: "https://courts.delaware.gov/" },
    { icon: "ri-global-line", label: "Order Online — VitalChek", desc: "Order Delaware birth, death, marriage, and divorce certificates online through VitalChek for University of Delaware area residents.", url: "https://www.vitalchek.com/" },
  ],
};